import { Controller } from "@hotwired/stimulus"

// Command palette — Cmd-K / Ctrl-K to open, fuzzy search businesses/users/invoices,
// keyboard-navigable. No external dependency.
//
// Targets: dialog, input, results
// Actions: open, close, search, navigate
export default class extends Controller {
  static targets = ["dialog", "input", "results"]
  static values  = { searchUrl: String }

  connect() {
    this.boundKeyHandler = this.handleGlobalKey.bind(this)
    document.addEventListener("keydown", this.boundKeyHandler)
    this.activeIndex = 0
    this.searchTimeout = null
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundKeyHandler)
  }

  handleGlobalKey(e) {
    // Cmd-K or Ctrl-K to open
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
      e.preventDefault()
      this.open()
    }
    // Escape to close (handled natively by <dialog>, but defensive)
    if (e.key === "Escape" && this.dialogTarget.open) {
      this.close()
    }
  }

  open() {
    if (!this.dialogTarget.open) {
      this.dialogTarget.showModal()
      this.inputTarget.value = ""
      this.resultsTarget.innerHTML = this.staticActionsHtml()
      this.activeIndex = 0
      setTimeout(() => this.inputTarget.focus(), 50)
    }
  }

  close() {
    if (this.dialogTarget.open) this.dialogTarget.close()
  }

  search(event) {
    const q = event.target.value.trim()

    clearTimeout(this.searchTimeout)

    if (q.length < 2) {
      this.resultsTarget.innerHTML = this.staticActionsHtml()
      this.activeIndex = 0
      return
    }

    this.searchTimeout = setTimeout(() => this.performSearch(q), 150)
  }

  async performSearch(q) {
    try {
      const res = await fetch(`${this.searchUrlValue}?q=${encodeURIComponent(q)}`, {
        headers: { "Accept": "application/json" }
      })
      if (!res.ok) throw new Error(`Search failed: ${res.status}`)

      const data = await res.json()
      this.renderResults(data.results || {})
      this.activeIndex = 0
    } catch (err) {
      console.error("[command-palette]", err)
      this.resultsTarget.innerHTML = `<p class="px-4 py-3 text-sm text-rose-500">Search failed.</p>`
    }
  }

  renderResults(results) {
    const { businesses = [], users = [], invoices = [] } = results
    const total = businesses.length + users.length + invoices.length

    if (total === 0) {
      this.resultsTarget.innerHTML = `<p class="px-4 py-6 text-sm text-slate-500 text-center">No matches.</p>`
      return
    }

    let html = ""

    if (businesses.length) {
      html += `<div class="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Businesses</div>`
      businesses.forEach(b => {
        html += this.itemHtml(b.url, "domain", b.name, b.slug, b.status)
      })
    }

    if (users.length) {
      html += `<div class="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400 mt-2">Users</div>`
      users.forEach(u => {
        html += this.itemHtml(u.url, "person", u.email, u.business || u.name || "")
      })
    }

    if (invoices.length) {
      html += `<div class="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400 mt-2">Invoices</div>`
      invoices.forEach(i => {
        // Sandbox hits look identical to real filings by invoice number alone —
        // say which dataset the match came from, the way the tables do.
        const subtitle = i.environment === "Sandbox" ? `${i.business} · Test` : i.business
        html += this.itemHtml(i.url, "receipt", i.number, subtitle, i.status)
      })
    }

    this.resultsTarget.innerHTML = html
  }

  staticActionsHtml() {
    const actions = [
      { url: "/admin",                  icon: "dashboard", label: "Dashboard" },
      { url: "/admin/businesses",       icon: "domain",    label: "Businesses" },
      { url: "/admin/users",            icon: "group",     label: "Users" },
      { url: "/admin/invoices",         icon: "receipt",   label: "Invoice monitor" },
      { url: "/admin/invoices/failed",  icon: "error",     label: "Failed invoices" },
      { url: "/admin/billing_invoices", icon: "request_quote", label: "Billing invoices" },
      { url: "/admin/fbr/api_calls",    icon: "api",       label: "FBR API log" },
      { url: "/admin/fbr/health",       icon: "monitoring",label: "FBR health" },
      { url: "/admin/audit_logs",       icon: "shield",    label: "Audit log" }
    ]
    let html = `<div class="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Go to</div>`
    actions.forEach(a => {
      html += this.itemHtml(a.url, a.icon, a.label, "")
    })
    return html
  }

  itemHtml(url, icon, primary, secondary, badge) {
    const badgeHtml = badge ? `<span class="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 ml-2">${this.escapeHtml(badge)}</span>` : ""
    return `
      <a href="${url}" class="cmd-item flex items-center gap-3 px-3 py-2 hover:bg-slate-100 rounded-lg text-sm">
        <span class="material-symbols-outlined text-slate-400 text-[18px]">${icon}</span>
        <div class="flex-1 min-w-0">
          <p class="text-slate-800 truncate">${this.escapeHtml(primary)}</p>
          ${secondary ? `<p class="text-xs text-slate-500 truncate">${this.escapeHtml(secondary)}</p>` : ""}
        </div>
        ${badgeHtml}
      </a>
    `
  }

  escapeHtml(s) {
    if (s == null) return ""
    return String(s).replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
  }

  navigate(event) {
    const items = this.resultsTarget.querySelectorAll(".cmd-item")
    if (items.length === 0) return

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.activeIndex = (this.activeIndex + 1) % items.length
      this.highlight(items)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.activeIndex = (this.activeIndex - 1 + items.length) % items.length
      this.highlight(items)
    } else if (event.key === "Enter") {
      event.preventDefault()
      const active = items[this.activeIndex]
      if (active) window.location.href = active.getAttribute("href")
    }
  }

  highlight(items) {
    items.forEach((el, i) => {
      if (i === this.activeIndex) {
        el.classList.add("bg-slate-100")
        el.scrollIntoView({ block: "nearest" })
      } else {
        el.classList.remove("bg-slate-100")
      }
    })
  }
}
